import type { IContainer } from '../interface/container';
import { bindLog, injectLog, unbindLog } from '../interface/log';
import cache from './impl/cache';
import callback from './impl/callback';
import channel from './impl/channel';
import fetchHelper from './impl/fetch';
import logger from './impl/log';
import regexJSON from './impl/regex';
import requestJSON from './impl/requestJSON';
import responseJSON from './impl/responseJSON';
import kvStorage from './impl/settingsStorage';
import workboxUtils from './impl/workboxUtils';
import zip from './impl/zip';
import { bindCacheStorage, injectCacheStorage, unbindCacheStorage } from './interface/cache';
import {
    bindCallbackBuilder,
    injectCallbackBuilder,
    unbindCallbackBuilder,
} from './interface/callback';
import {
    bindChannelController,
    injectChannelController,
    unbindChannelController,
} from './interface/channel';
import { bindFetchHelper, injectFetchHelper, unbindFetchHelper } from './interface/fetch';
import {
    bindRegexSerializer,
    injectRegexSerializer,
    unbindRegexSerializer,
} from './interface/regex';
import {
    bindRequestSerializer,
    injectRequestSerializer,
    unbindRequestSerializer,
} from './interface/requestSerializer';
import {
    bindResponseSerializer,
    injectResponseSerializer,
    unbindResponseSerializer,
} from './interface/responseSerializer';
import {
    bindSettingsStorage,
    injectSettingsStorage,
    unbindSettingsStorage,
} from './interface/settingsStorage';
import { bindWorkboxUtils, injectWorkboxUtils, unbindWorkboxUtils } from './interface/workboxUtils';
import { bindZip, injectZip, IZip, unbindZip } from './interface/zip';

const bindJobs: Array<{
    bind: (container: IContainer) => void;
    remove: (container: IContainer) => void;
    expose: (srcContainer: IContainer, dstContainer: IContainer) => void;
}> = [
    {
        bind: (container: IContainer) => bindCallbackBuilder(callback(container), container),
        remove: (container: IContainer) => unbindCallbackBuilder(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindCallbackBuilder(injectCallbackBuilder(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindChannelController(channel(container), container),
        remove: (container: IContainer) => unbindChannelController(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindChannelController(injectChannelController(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindCacheStorage(cache, container),
        remove: (container: IContainer) => unbindCacheStorage(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindCacheStorage(injectCacheStorage(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindFetchHelper(fetchHelper, container),
        remove: (container: IContainer) => unbindFetchHelper(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindFetchHelper(injectFetchHelper(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindSettingsStorage(kvStorage(container), container),
        remove: (container: IContainer) => unbindSettingsStorage(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindSettingsStorage(injectSettingsStorage(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindRegexSerializer(regexJSON, container),
        remove: (container: IContainer) => unbindRegexSerializer(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindRegexSerializer(injectRegexSerializer(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindRequestSerializer(requestJSON(), container),
        remove: (container: IContainer) => unbindRequestSerializer(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindRequestSerializer(injectRequestSerializer(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindResponseSerializer(responseJSON(), container),
        remove: (container: IContainer) => unbindResponseSerializer(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindResponseSerializer(injectResponseSerializer(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindLog(logger(), container),
        remove: (container: IContainer) => unbindLog(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindLog(injectLog(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => bindWorkboxUtils(workboxUtils, container),
        remove: (container: IContainer) => unbindWorkboxUtils(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindWorkboxUtils(injectWorkboxUtils(srcContainer), dstContainer),
    },

    {
        bind: (container: IContainer) => {
            let val: IZip | null = null;

            const resolver = () => {
                return new Promise<IZip>(async function (resolve, reject) {
                    let rejection = null;

                    try {
                        if (!val) {
                            val = zip;
                        }
                    } catch (error) {
                        rejection = error;
                    }

                    if (val) {
                        resolve(val);
                    } else {
                        reject(rejection || 'Import failed for zip');
                    }
                });
            };

            bindZip(resolver, container);
        },
        remove: (container: IContainer) => unbindZip(container),
        expose: (srcContainer: IContainer, dstContainer: IContainer) =>
            bindZip(() => injectZip(srcContainer), dstContainer),
    },
];

const module = {
    bind: (container: IContainer) => bindJobs.forEach(binder => binder.bind(container)),
    unbind: (container: IContainer) => bindJobs.forEach(binder => binder.remove(container)),
    expose: (srcContainer: IContainer, dstContainer: IContainer) =>
        bindJobs.forEach(binder => binder.expose(srcContainer, dstContainer)),
};

export default module;
